import { Container, Sprite, Texture } from 'pixi.js';
import { StudioSceneShape } from '../../../shape/shapes/general/root/studio-scene-shape';
import { saveShape } from '../studio.actions';
import { cloneDeep } from 'lodash';
import {
  StudioShapeBackground,
  StudioShapeDescriptor,
} from '../text/studio-text-shape';
import { StudioAnimation, StudioCell } from '..';
import gsap from 'gsap';
import { RectangleElement } from '../../../shape/shapes/primitive/rectangle-element';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';

export interface StudioShapeConfig {
  preview?: boolean;
}

export class StudioShape<
  T extends StudioShapeDescriptor = StudioShapeDescriptor,
> {
  container: Container;
  circleContainer: Container;
  loadPromise: Promise<void> = Promise.resolve();
  get x() {
    return 0;
  }
  get y() {
    return 0;
  }

  get cell() {
    return this.descriptor.cell;
  }
  get groupShapeParent() {
    return null;
  }

  get cs() {
    return this.shape.cs;
  }

  get service() {
    return this.shape.studioService.shapeService;
  }
  get studioService() {
    return this.shape.studioService;
  }

  get paddingValue() {
    return this.descriptor.cell.padding == undefined
      ? 0
      : (this.descriptor.cell.padding as number);
  }

  get absPadding() {
    return this.shape.screenWidth * this.paddingValue;
  }

  get grossDims() {
    const { w, h } = this.descriptor.cell || { w: 1, h: 1 };
    return [this.shape.screenWidth * w, this.shape.screenHeight * h];
  }

  get grossCoords() {
    const { x, y } = this.descriptor.cell;
    const [screenX, screenY] = [
      this.shape.screenWidth,
      this.shape.screenHeight,
    ];
    return [screenX * x, screenY * y];
  }

  get cellDims() {
    const [w, h] = this.grossDims;
    const [x, y] = this.grossCoords;
    return { x, y, w, h };
  }

  get padding(): [number, number] {
    if (!this.descriptor.cell.padding) {
      return [0, 0];
    }

    if (Array.isArray(this.descriptor.cell.padding)) {
      return this.descriptor.cell.padding as [number, number];
    }

    return [this.descriptor.cell.padding, this.descriptor.cell.padding];
  }

  get netDims() {
    const [w, h] = this.grossDims;
    const [px, py] = this.padding;
    return [
      w - 2 * px * this.shape.screenWidth,
      h - 2 * py * this.shape.screenHeight,
    ];
  }

  cellRc: RectangleController;
  constructor(
    protected readonly shape: StudioSceneShape,
    public id: string,
    public descriptor: T,
    protected readonly config?: StudioShapeConfig,
  ) {
    this.container = new Container();
    this.circleContainer = new Container();
    this.shape.container.addChild(this.container);
    // -- // -- // -- // -- // -- // -- // -- // -- // -- //
    this.shape.circleContainer.addChild(this.circleContainer);
    this.initBackgroundElement(this.descriptor.cell?.background);
  }

  dragTookPlace = false;
  unmovedCoords: [number, number];

  pseudoAnimation(animation: StudioAnimation): gsap.core.Timeline {
    const { duration } = animation;
    const timeline = gsap.timeline();

    timeline.add(gsap.to({}, { duration: duration || 1 }));

    const { next, paralell } = animation;
    if (next) {
      const nextTimeline = this.pseudoAnimation(next);
      const nextOffset = next.overlap
        ? `-=${next.overlap}`
        : `+=${next.delay || 0}`;
      timeline.add(nextTimeline, nextOffset);
    }

    if (paralell) {
      const { overlap, delay } = paralell;
      const paralellTimeline = this.pseudoAnimation(paralell);
      const offset = overlap ? `-=${overlap}` : delay || 0;
      timeline.add(paralellTimeline, offset);
    }

    return timeline;
  }

  reset() {}

  update(descriptor: T) {
    this.descriptor = descriptor;
  }

  startBaseDrag() {
    this.dragTookPlace = false;
    this.unmovedCoords = [
      this.container.x - this.shape.xOffset,
      this.container.y - this.shape.yOffset,
    ];
  }
  drag(dx: number, dy: number) {
    this.dragTookPlace = true;
    const [x, y] = this.unmovedCoords;
    this.setPosition(x + dx, y + dy);
  }

  endDrag() {
    if (!this.dragTookPlace) return;
    // Cell's x, y are relative to the shape's width and height
    this.descriptor.cell.x =
      (this.container.x - this.shape.xOffset) / this.shape.screenWidth;
    this.descriptor.cell.y =
      (this.container.y - this.shape.yOffset) / this.shape.screenHeight;
    this.descriptor.cell.custom = true;

    this.shape.scene.shapes[this.id] = this.descriptor;
    this.save();
  }

  setPosition(x: number, y: number) {
    this.container.x = this.shape.xOffset + x;
    this.container.y = this.shape.yOffset + y;
    this.circleContainer.x = this.shape.xOffset + x;
    this.circleContainer.y = this.shape.yOffset + y;
  }

  save() {
    this.shape.store.dispatch(
      saveShape({
        shapeId: this.id,
        shape: cloneDeep(this.descriptor),
      }),
    );
  }

  backgroundElement: RectangleElement;

  initBackgroundElement(background: StudioShapeBackground): RectangleElement {
    this.backgroundElement?.remove();
    if (!background) return;
    const [width, height] = this.netDims;
    const { type, radius, color, opacity, gradient, stroke, padding } =
      background;

    if (type === 'gradient' && gradient) {
      const gradientStops = gradient.colors?.map(c => ({ color: c })) || [];
      this.backgroundElement = new RectangleElement(this, this.container, {
        width,
        height,
        r: radius || 0,
        gradient: true,
        gradientDirection: gradient.direction,
        gradientStops,
        stroke: stroke?.color,
        'stroke-width': stroke?.width,
      });
    } else {
      const { x, y, w, h } = this.cellDims;

      const paddingOffet = 0;

      this.backgroundElement = new RectangleElement(
        this,
        this.shape.container,
        {
          position: { x: x + paddingOffet, y: y + paddingOffet },
          width: w - 2 * paddingOffet,
          height: h - 2 * paddingOffet,
          r: radius || 0,
          fill: color || '#ffffff',
          'fill-opacity': opacity || 1,
          stroke: stroke?.color,
          'stroke-width': stroke?.width,
        },
      );
    }

    return this.backgroundElement;
  }

  createGradientMaskSprite(w, h) {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 2;

    const ctx = c.getContext('2d');

    const g = ctx.createLinearGradient(0, 0, c.width, 0);

    g.addColorStop(0.0, 'rgba(255,255,255,1)');
    g.addColorStop(0.7, 'rgba(255,255,255,1)');
    g.addColorStop(0.85, 'rgba(255,255,255,0)');
    g.addColorStop(1.0, 'rgba(255,255,255,0)');

    ctx.fillStyle = g;
    ctx.fillRect(0, 0, c.width, c.height);

    const tex = Texture.from(c);
    return new Sprite(tex);
  }

  getAnimationFunction(key: string): StudioAnimation {
    return this.descriptor.animationFunctions?.[key];
  }

  gradientMaskAnimation(animation: StudioAnimation) {
    const { gradientMask } = animation;
    if (!gradientMask) return;
    const isHorizontal = gradientMask === 'left' || gradientMask === 'right';
    const [width, height] = this.grossDims;
    const maskW = isHorizontal ? width * 2 : width;
    const maskH = isHorizontal ? height : height * 2;

    const sprite = this.createGradientMaskSprite(maskW, maskH);

    sprite.width = maskW;
    sprite.height = maskH;
    switch (gradientMask) {
      case 'left':
        sprite.x = -maskW;
        break;
      case 'right':
        sprite.x = maskW;
        break;
      case 'top':
        sprite.y = -maskH;
        break;
      case 'bottom':
        sprite.y = maskH;
        break;
    }

    this.container.addChild(sprite);
    // -- //

    return sprite;
  }

  applyGradientMask(animation: StudioAnimation) {
    // todo - implement by shape
  }

  cellDimsnByCell(cell: StudioCell) {
    const [screenX, screenY] = [
      this.shape.screenWidth,
      this.shape.screenHeight,
    ];
    const { x, y, w, h } = cell;
    return { x: screenX * x, y: screenY * y, w: screenX * w, h: screenY * h };
  }

  alignInCell(cell: StudioCell) {
    // implement in the child class //
  }

  removed = false;
  remove() {
    this.removed = true;
    this.container.destroy({ children: true });
    this.circleContainer.destroy({ children: true });
  }

  timeline: gsap.core.Timeline;
  gsapAnimateStep(
    animation: StudioAnimation,
    timeline?: gsap.core.Timeline,
    force = false,
  ) {
    timeline ||= gsap.timeline();
    const { next, paralell, sceneTransition, type, duration } = animation;

    if (animation.startPosition) {
      //   console.log('alignInCell', animation.startPosition);
      //   this.alignInCell(animation.startPosition);
      // }
      if (next) {
        const nextTarget = this.shape.getTargetIRI(next.target);
        const nextShape = this.shape.shapes[nextTarget];
        if (nextShape) {
          // if (next.startPosition && 'alignInCell' in nextShape) {
          //   (nextShape as any).alignInCell(next.startPosition);
          // }
          const nextTimeline = nextShape.gsapAnimateStep(next);
          const { overlap, delay } = next;
          const offset = overlap ? `-=${overlap}` : `+=${delay || 0}`;
          timeline.add(nextTimeline, offset);
        } else {
          console.warn('Next shape not found', next.target);
        }
      }

      if (paralell) {
        const paralellTarget = this.shape.getTargetIRI(paralell.target);
        const paralellShape = this.shape.shapes[paralellTarget];
        if (paralellShape) {
          if (paralell.startPosition?.cell && 'alignInCell' in paralellShape) {
            (paralellShape as any).alignInCell(paralell.startPosition.cell);
          }
          const paralellTimeline = paralellShape.gsapAnimateStep(paralell);
          timeline.add(paralellTimeline, paralell.delay || 0);
        } else {
          // console.warn('Paralell shape not found', paralell.target);
        }
      }

      // -- //

      return timeline;
    }
  }
}
